/**
 * useLogEvents Hook
 *
 * WebSocket hook for subscribing to BRUTAL log events.
 * Provides real-time log streaming with filtering capabilities.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { z } from 'zod';
import { getRpcClient } from '../lib/rpc-client';

// ═══════════════════════════════════════════════════════════════
// LOG SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** Log level enumeration */
export const LogLevelSchema = z.enum(['debug', 'info', 'warn', 'error']);
export type LogLevel = z.infer<typeof LogLevelSchema>;

/** BRUTAL log entry schema - matches BrutalLogEntry from design doc */
export const BrutalLogEntrySchema = z.object({
  timestamp: z.string(),
  level: LogLevelSchema,
  deployment_id: z.string().nullable(),
  artifact: z.string().nullable(),
  environment: z.string().nullable(),
  host: z.string().nullable(),
  message: z.string(),
  metadata: z.record(z.string(), z.unknown()),
});

export type BrutalLogEntry = z.infer<typeof BrutalLogEntrySchema>;

/** Log query response schema */
const LogQueryResponseSchema = z.object({
  logs: z.array(BrutalLogEntrySchema),
  total: z.number(),
  hasMore: z.boolean(),
});

// ═══════════════════════════════════════════════════════════════
// FILTER TYPES
// ═══════════════════════════════════════════════════════════════

export interface LogFilters {
  environment: string | null;
  artifact: string | null;
  level: LogLevel | null;
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

export interface UseLogEventsOptions {
  /** Maximum number of logs to keep in memory */
  maxLogs: number | null;
  /** Whether to auto-fetch historical logs on connect */
  autoFetch: boolean;
  /** Initial filters */
  filters: LogFilters | null;
}

export interface UseLogEventsResult {
  logs: BrutalLogEntry[];
  isLoading: boolean;
  error: string | null;
  filters: LogFilters;
  setFilters: (filters: LogFilters) => void;
  availableEnvironments: string[];
  availableArtifacts: string[];
  refreshLogs: () => Promise<void>;
  clearLogs: () => void;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

/**
 * Hook for subscribing to log events via WebSocket.
 * Maintains local state of logs with filtering support.
 */
export function useLogEvents(options: UseLogEventsOptions | null = null): UseLogEventsResult {
  const maxLogs = options?.maxLogs ?? 500;
  const autoFetch = options?.autoFetch ?? true;
  const initialFilters = options?.filters ?? { environment: null, artifact: null, level: null };

  const [logs, setLogs] = useState<BrutalLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LogFilters>(initialFilters);
  const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([]);
  const [availableArtifacts, setAvailableArtifacts] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);

  // ─────────────────────────────────────────────────────────────────
  // LOG HELPER
  // ─────────────────────────────────────────────────────────────────

  const matchesFilters = useCallback(
    (log: BrutalLogEntry): boolean => {
      if (filters.environment !== null && log.environment !== filters.environment) {
        return false;
      }
      if (filters.artifact !== null && log.artifact !== filters.artifact) {
        return false;
      }
      if (filters.level !== null && log.level !== filters.level) {
        return false;
      }
      return true;
    },
    [filters],
  );

  const addLog = useCallback(
    (log: BrutalLogEntry) => {
      // Update available filters from incoming logs
      if (log.environment !== null && log.environment !== '') {
        setAvailableEnvironments((prev) =>
          prev.includes(log.environment as string) ? prev : [...prev, log.environment as string],
        );
      }
      if (log.artifact !== null && log.artifact !== '') {
        setAvailableArtifacts((prev) =>
          prev.includes(log.artifact as string) ? prev : [...prev, log.artifact as string],
        );
      }

      // Only add if matches current filters
      if (!matchesFilters(log)) {
        return;
      }

      setLogs((prev) => {
        const updated = [...prev, log];
        // Trim to maxLogs
        if (updated.length > maxLogs) {
          return updated.slice(-maxLogs);
        }
        return updated;
      });
    },
    [matchesFilters, maxLogs],
  );

  // ─────────────────────────────────────────────────────────────────
  // FETCH LOGS
  // ─────────────────────────────────────────────────────────────────

  const fetchLogs = useCallback(
    async (append: boolean = false): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const client = getRpcClient();
        const offset = append ? offsetRef.current : 0;
        const response = await client.call(
          'log.query',
          {
            environment: filters.environment,
            artifact: filters.artifact,
            level: filters.level,
            limit: 100,
            offset,
          },
          LogQueryResponseSchema,
        );

        // Extract unique environments and artifacts
        const envSet = new Set<string>();
        const artSet = new Set<string>();
        for (const log of response.logs) {
          if (log.environment !== null && log.environment !== '') {
            envSet.add(log.environment);
          }
          if (log.artifact !== null && log.artifact !== '') {
            artSet.add(log.artifact);
          }
        }
        setAvailableEnvironments((prev) => [...new Set([...prev, ...envSet])]);
        setAvailableArtifacts((prev) => [...new Set([...prev, ...artSet])]);

        if (append) {
          setLogs((prev) => [...response.logs.reverse(), ...prev]);
        } else {
          setLogs(response.logs.reverse());
        }
        setHasMore(response.hasMore);
        offsetRef.current = offset + response.logs.length;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch logs';
        // Handle "Method not found" gracefully - log.query may not be implemented
        if (message.includes('Method not found') || message.includes('log.query')) {
          // Not an error - just not available
          setError(null);
        } else {
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  const refreshLogs = useCallback(async (): Promise<void> => {
    offsetRef.current = 0;
    await fetchLogs(false);
  }, [fetchLogs]);

  const loadMore = useCallback(async (): Promise<void> => {
    await fetchLogs(true);
  }, [fetchLogs]);

  // ─────────────────────────────────────────────────────────────────
  // CLEAR LOGS
  // ─────────────────────────────────────────────────────────────────

  const clearLogs = useCallback(() => {
    setLogs([]);
    offsetRef.current = 0;
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // SUBSCRIBE TO EVENTS
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const client = getRpcClient();
    const unsubscribers: Array<() => void> = [];
    let isMounted = true;

    // Subscribe to log events
    unsubscribers.push(
      client.subscribe('log.entry', (data) => {
        try {
          const log = BrutalLogEntrySchema.parse(data);
          addLog(log);
        } catch {
          // Ignore invalid event
        }
      }),
    );

    // Auto-fetch logs on mount - wait for connection first
    if (autoFetch) {
      client
        .waitForReady(10000)
        .then(() => {
          if (isMounted) {
            refreshLogs();
          }
        })
        .catch((err) => {
          if (isMounted) {
            const message = err instanceof Error ? err.message : 'Connection failed';
            setError(message);
            setIsLoading(false);
          }
        });
    } else {
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      isMounted = false;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [autoFetch, addLog, refreshLogs]);

  // Refresh when filters change
  useEffect(() => {
    refreshLogs();
  }, [filters.environment, filters.artifact, filters.level]);

  return {
    logs,
    isLoading,
    error,
    filters,
    setFilters,
    availableEnvironments,
    availableArtifacts,
    refreshLogs,
    clearLogs,
    loadMore,
    hasMore,
  };
}
